/**
 * Filing a closed trade without anybody pressing anything.
 *
 * A window that has run its course has no judgement left in it: nobody countered it out, nobody
 * vetoed it, so it stands. Tim, 3 September: "when window closes, the trades can be pushed to the
 * log automatically." The card used to say "It reaches the log when a commissioner files it",
 * which described the implementation rather than the rule.
 *
 * WHY A COMMISSIONER'S SESSION STILL DOES THE WORK. `firestore.rules` allows only a commissioner
 * to write `trades`, and that is not incidental — the ledger is eleven years of record. So the act
 * is still performed under a commissioner's credentials; what changed is that it no longer waits
 * for him to notice a button. Any commissioner with the app open files everything that is due,
 * which for this league is most days; `functions/src/fileTrades.ts` covers the days nobody opens
 * it, every fifteen minutes, through this same file and the same `settlePlan`.
 *
 * WHY THERE IS A LEASE AND NOT A TRANSACTION. Two commissioners opening Trades in the same second
 * would otherwise both settle the same trade. Most of that is harmless by construction: the ledger
 * id is derived from the live id, so both write the same row with the same contents, and the
 * reallocation is a pure function of the state both are reading. What is NOT harmless is the pair
 * of them reading declarations while the other is halfway through writing them, which can fit a
 * slate against a board that never existed.
 *
 * So a session marks the trade before it starts and the mark expires. It is a lease rather than a
 * transaction because the cost of the rare miss is a duplicate audit note, and the cost of a
 * transaction is a write path that a commissioner on a dropping connection can wedge: a claim that
 * never clears is a trade nobody can file, which is worse than the thing it prevents.
 */

import { awaitingFiling, type LiveTrade, type WindowCtx } from './tradeWindow.js';

/**
 * How long a claim stands.
 *
 * Long enough for a settle to finish — the ledger row, both declarations and the audit notes, on a
 * phone connection — and short enough that a session that died mid-way does not hold a trade past
 * the point anybody would wait. Two minutes is roughly ten times the slow case.
 */
export const LEASE_MS = 2 * 60 * 1000;

/** A trade mid-settle, and who is doing it. */
export interface Claim {
  by: string;
  /** ISO. */
  at: string;
}

/**
 * May this session start settling?
 *
 * Yes when nobody holds it, when the holder is me (a retry after a failure is mine to make), and
 * when the claim has gone stale. An unparseable claim is treated as stale rather than as a lock,
 * because a trade held by a malformed field is a trade nobody can ever file.
 */
export function claimable(claim: Claim | null | undefined, me: string, now: number): boolean {
  if (!claim?.at) return true;
  if (claim.by?.toUpperCase() === me.toUpperCase()) return true;
  const at = Date.parse(claim.at);
  if (Number.isNaN(at)) return true;
  return now - at >= LEASE_MS;
}

/**
 * What this session should file, oldest first.
 *
 * Oldest first because a trade can be the parent of a later one — a counter accepted inside its
 * window produces a deal that inherits it — and settling the parent first is the order a person
 * filing them by hand would use.
 *
 * `done` is the set already attempted in this session. A settle that throws is not retried on the
 * next render: it would loop against whatever refused it, and the button is still there.
 */
export function toFile(
  trades: readonly LiveTrade[],
  ctx: WindowCtx,
  me: string,
  done: ReadonlySet<string>,
  now = Date.now(),
): LiveTrade[] {
  return trades
    .filter((t) => !done.has(t.id))
    .filter((t) => awaitingFiling(t, ctx))
    .filter((t) => claimable(t.settling, me, now))
    .sort((a, b) => (a.agreedAt ?? a.filedAt).localeCompare(b.agreedAt ?? b.filedAt));
}
